import React from 'react';

import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { useDeleteNoteMutation } from 'src/common/apis/FlorenceApi';
import { SaveSheet } from 'src/common/types/SaveSheet';
import { displayDateString } from 'src/common/utils/DateUtils';
import { getSheetName } from 'src/florence/utils/getSheetName';

import { ReactComponent as CancelIconBlue } from '../../../../common/assets/icons/cancel_icon_blue.svg';
import { ReactComponent as PaperIcon } from '../../../../common/assets/icons/paper_icon_small_gray.svg';

type Props = {
  note: SaveSheet;
  onClose: () => void;
};

const NoteDeletePopup: React.FC<Props> = ({ onClose, note }) => {
  const [deleteNote, { isLoading }] = useDeleteNoteMutation();

  const deleteSheet = async () => {
    await deleteNote(note);
    onClose();
  };

  return (
    <Box
      sx={{
        maxWidth: '575px',
        minHeight: '418px',
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flex: 1,
        borderRadius: '15px',
        padding: '30px',
        flexDirection: 'column',
      }}
    >
      <IconButton
        disabled={isLoading}
        onClick={onClose}
        sx={{ alignSelf: 'flex-start', justifySelf: 'right' }}
      >
        <CancelIconBlue />
      </IconButton>
      <Typography
        sx={{
          alignSelf: 'center',
          whiteSpace: 'break-spaces',
          textAlign: 'center',
          mb: '24px',
        }}
        color='text.primary'
        variant='h5'
      >
        Are you sure you want to{'\n'}delete this sheet?
      </Typography>
      <Box
        sx={{
          backgroundColor: 'divider',
          borderRadius: '16px',
          height: '60px',
          p: '12px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <PaperIcon />
        <Box sx={{ flexDirection: 'column', ml: '12px', alignSelf: 'center' }}>
          <Typography color='text.primary'>{getSheetName(note)}</Typography>
          <Typography variant='body2' color='textFlorence.main'>
            {note.createdAt
              ? displayDateString(note.createdAt, true)
              : '01/01/2000'}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          alignSelf: 'center',
          display: 'flex',
          marginTop: '20px',
        }}
      >
        <Button
          disabled={isLoading}
          onClick={onClose}
          disableElevation
          color='appGray2'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
            marginRight: '15px',
          }}
        >
          <Typography color='white'>Cancel</Typography>
        </Button>
        <LoadingButton
          onClick={deleteSheet}
          loading={isLoading}
          disableElevation
          color='error'
          variant='contained'
          sx={{
            borderRadius: '50px',
            width: '180px',
            height: '60px',
          }}
        >
          <Typography color='white'>Delete</Typography>
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default NoteDeletePopup;
